import {useEffect, useRef} from 'react'

// «افزایش موجودی و ادامه» (user request 2026-10-17): a step that was short of wallet credit is remembered in this tab
// before the payer goes to the gateway, and goes on by itself on the same page once the credit is in the wallet.
// sessionStorage keeps it across the gateway's round trip in the same tab only; it is never a second copy of the
// request on the server, and a step is resumed at most once.

export type WalletResume = {
  companyId: number
  /** Which step: the component that resumes it listens for this name. */
  action: string
  /** What the step needs to run again (a feature key, a package id …); never a password or a file. */
  payload?: unknown
  /** The step in the user's words: «فعال‌سازی پیش‌فاکتور». */
  label: string
  /** The panel page the gateway returns to. */
  returnPath: string
  at: number
  /** Set when the charge's credit is in the wallet: the step may run now. */
  readyFor?: string
}

export const WALLET_RESUME_EVENT = 'megapay:wallet-resume-ready'

const KEY = 'megapay.wallet-resume.v1'
/** A gateway round trip takes minutes; an old entry is a payment that never came back. */
const MAX_AGE_MS = 2 * 60 * 60 * 1000

const storage = (): Storage | undefined => {
  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}

export const readWalletResume = (): WalletResume | undefined => {
  const store = storage()
  try {
    const raw = store?.getItem(KEY)
    if (!raw) return undefined
    const resume = JSON.parse(raw) as WalletResume
    if (!resume || typeof resume.action !== 'string' || Date.now() - resume.at > MAX_AGE_MS) {
      store?.removeItem(KEY)
      return undefined
    }
    return resume
  } catch {
    store?.removeItem(KEY)
    return undefined
  }
}

export const clearWalletResume = () => {
  try {
    storage()?.removeItem(KEY)
  } catch {
    // Nothing to forget.
  }
}

/** Remembers the step before the browser leaves for the gateway (one at a time: the newest wins). */
export const rememberWalletResume = (resume: Omit<WalletResume, 'at' | 'readyFor'>) => {
  try {
    storage()?.setItem(KEY, JSON.stringify({...resume, at: Date.now()}))
  } catch {
    // Private mode or a full store: the payer comes back and repeats the step by hand.
  }
}

/** The credit of payment publicId is in the wallet: the remembered step may run, once. */
export const markWalletResumeReady = (publicId: string): WalletResume | undefined => {
  const resume = readWalletResume()
  if (!resume) return undefined
  const ready = {...resume, readyFor: publicId}
  try {
    storage()?.setItem(KEY, JSON.stringify(ready))
  } catch {
    return undefined
  }
  window.dispatchEvent(new CustomEvent(WALLET_RESUME_EVENT, {detail: {action: resume.action}}))
  return ready
}

/** The step's payload when it is ready for this action and business; taking it forgets it, so it runs once. */
export const takeWalletResume = (
  action: string,
  companyId: number
): {payload: unknown} | undefined => {
  const resume = readWalletResume()
  if (!resume || !resume.readyFor || resume.action !== action || resume.companyId !== companyId)
    return undefined
  clearWalletResume()
  return {payload: resume.payload}
}

/** The page the payer is on, without a gateway's ?payment=. */
export const currentReturnPath = (): string => {
  const url = new URL(window.location.href)
  url.searchParams.delete('payment')
  return url.pathname + url.search
}

/**
 * Runs <paramref name="handler"/> once with the remembered step's payload when the credit of the charge made for it is
 * in the wallet: at mount when it already is, or when WalletReturnHost says so.
 */
export const useWalletResume = (
  action: string,
  companyId: number,
  handler: (payload: unknown) => void,
  enabled = true
) => {
  const handlerRef = useRef(handler)
  handlerRef.current = handler
  useEffect(() => {
    if (!enabled || !companyId) return undefined
    const check = () => {
      const taken = takeWalletResume(action, companyId)
      if (taken) handlerRef.current(taken.payload)
    }
    check()
    window.addEventListener(WALLET_RESUME_EVENT, check)
    return () => window.removeEventListener(WALLET_RESUME_EVENT, check)
  }, [action, companyId, enabled])
}
